// TelcoTrack - GeoJSON Import Constants
// Constants for GeoJSON import functionality and drawing type filters

// =============================================================================
// LINE GROUPING (S-JTSK meters) – import and layer reassign
// =============================================================================

/** Tolerance (m) for "same point" when grouping lines that share endpoints. */
export const SAME_POINT_TOLERANCE_M = 0.01;

/** Import: max distance (m) between objects of the same layer to treat as duplicate (point centroid or both line endpoints). */
export const IMPORT_SPATIAL_DEDUP_RADIUS_M = 5;

/** Max distance (m) between groups to merge them (imaginary lines parallel and within this distance). */
export const PARALLEL_MAX_DISTANCE_M = 5;

/** Max angle difference (degrees) to consider two imaginary lines parallel. */
export const PARALLEL_MAX_ANGLE_DEG = 5;

/** Connectivity grouping (VYKOP, STAVAJICI): share point or point on segment. */
export const CONNECTIVITY_TOLERANCE_M = 0.01;

/**
 * Max endpoint gap (m) to chain source polylines before densify (CAD GeoJSON often splits one route).
 * 20 cm: CAD splits leave sub-decimetre gaps, but short stubs (odbočky) sitting ~30–50 cm from a
 * main-line break must stay separate — 1 m previously absorbed them into the main chain.
 */
export const CONNECTIVITY_ENDPOINT_MERGE_TOLERANCE_M = 0.2;

/** Dedup tolerance (m) for connectivity segment edges — strict; 5 m would drop parallel traces ~3–5 m apart. */
export const CONNECTIVITY_SEGMENT_DEDUP_TOLERANCE_M = 0.05;

/** Step (m) for densifying lines to points when creating connectivity segment objects (import/reassign). */
export const LINE_DENSIFY_STEP_M = 2;

/** Max points per line after densification; above this we skip densification to avoid memory/DB explosion. */
export const LINE_DENSIFY_MAX_POINTS = 50000;

// =============================================================================
// DRAWING TYPES
// =============================================================================

// Drawing type constants for Czech fiber optic network documentation
export const DRAWING_TYPES = [
  '1', // Výkopové práce
  '2',  // Instalace
] as const;

export type DrawingType = typeof DRAWING_TYPES[number];

// Mapping of drawing types to object types they should include
// Updated 2026-02-09 after migration 029 to new object types
export const DRAWING_TYPE_MAPPING = {
  '1': [
    // Výkopové práce - excavation LINE objects
    'VYKOP',
    'STAVAJICI',
    // Points visible in construction drawing
    'BUDOVA',
    'SPOJKOVISTE',
    'JAMA',
    // INFO available everywhere
    'INFO',
  ],
  '2': [
    // Instalace - installation POINT objects
    'ROZVADEC',
    'BUDOVA',
    'SPOJKOVISTE',
    'JAMA',
    // INFO available everywhere
    'INFO',
  ]
} as const;

// =============================================================================
// OBJECT TYPE SETS (shared across backend import, dashboard SQL, layer logic)
// =============================================================================

/** Line types grouped for connectivity (import / layer reassign). */
export const CONNECTIVITY_LINE_TYPES = ['VYKOP', 'STAVAJICI'] as const;

/** Object types that store excavation snapshot metadata on phase transition. */
export const EXCAVATION_SNAPSHOT_OBJECT_TYPES = ['VYKOP', 'JAMA'] as const;

/** Object types excluded from polygon dashboard status counts (informativní typy bez workflow). */
export const POLYGON_DASHBOARD_EXCLUDED_OBJECT_TYPES = ['UNASSIGNED', 'INFO', 'STAVAJICI'] as const;

/** Types a field user may delete from their own layer. */
export const OWN_DELETABLE_OBJECT_TYPES = ['INFO', 'SPOJKOVISTE', 'JAMA', 'KONCOVYBOD'] as const;

/** Connectable building types (includes legacy DB alias OBJEKT). */
export const LEGACY_OBJECT_TYPE_OBJEKT = 'OBJEKT' as const;
export const CONNECTABLE_OBJECT_TYPES = ['BUDOVA', LEGACY_OBJECT_TYPE_OBJEKT] as const;

export function isConnectableObjectType(type: string): boolean {
  return (CONNECTABLE_OBJECT_TYPES as readonly string[]).includes(type);
}

/** SQL IN-list fragments for raw queries (e.g. `type NOT IN (...)`). */
export const CONNECTABLE_OBJECT_TYPES_SQL_IN = CONNECTABLE_OBJECT_TYPES.map((t) => `'${t}'`).join(', ');
export const POLYGON_DASHBOARD_EXCLUDED_SQL_IN = POLYGON_DASHBOARD_EXCLUDED_OBJECT_TYPES.map((t) => `'${t}'`).join(', ');
export const CONNECTIVITY_LINE_TYPES_SQL_IN = CONNECTIVITY_LINE_TYPES.map((t) => `'${t}'`).join(', ');

export function isConnectivityLineType(type: string): boolean {
  return (CONNECTIVITY_LINE_TYPES as readonly string[]).includes(type);
}

